import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from '../lib/api';
import type { InvitationPreviewDto } from '../lib/types';

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    return String((err.body as { error: unknown }).error);
  }
  return fallback;
}

/**
 * Standalone page for someone who has an invite link but no account yet. Rendered outside the
 * app shell (like /login) because the visitor is unauthenticated by definition.
 */
export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const preview = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => apiClient.get<InvitationPreviewDto>(`/api/invitations/preview?token=${encodeURIComponent(token)}`),
    enabled: token.length > 0,
    retry: false,
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (password !== confirm) {
      setFormError('Those passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/api/invitations/accept', { token, password });
      setDone(true);
    } catch (err) {
      setFormError(errorMessage(err, 'Could not create your account. Try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6 w-full max-w-md">
        <h1 className="text-2xl font-bold text-brand-blue">ProjectCalendar</h1>

        {!token && (
          <p role="alert" className="text-sm text-red-700 mt-4">
            This link is missing its invitation token. Ask whoever invited you to send the full link.
          </p>
        )}

        {token && preview.isLoading && (
          <p className="text-sm text-slate-500 mt-4">Checking your invitation…</p>
        )}

        {token && preview.error && (
          <div className="mt-4 space-y-3">
            <p role="alert" className="text-sm text-red-700">
              {errorMessage(preview.error, 'That invitation link is invalid, expired, or already used.')}
            </p>
            <p className="text-sm text-slate-600">
              Ask an administrator to send you a new invitation.
            </p>
          </div>
        )}

        {done && (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-brand-charcoal">
              Your account is ready. Sign in with your email and the password you just set.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="w-full bg-brand-blue text-white rounded px-4 py-2 text-sm font-medium hover:bg-brand-blue/90"
            >
              Go to sign in
            </button>
          </div>
        )}

        {!done && preview.data && (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div>
              <p className="text-sm text-brand-charcoal">
                You've been invited as <strong>{preview.data.email}</strong>.
              </p>
              <p className="text-xs text-brand-charcoal/70 mt-1">
                Access level: {preview.data.roles.join(', ') || 'User'} · link expires{' '}
                {new Date(preview.data.expiresUtc).toLocaleDateString()}
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-brand-charcoal">
                Choose a password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
              />
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-brand-charcoal">
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
              />
            </div>

            {formError && (
              <p role="alert" className="text-sm text-red-700">{formError}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-orange text-white rounded px-4 py-2 text-sm font-medium hover:bg-brand-orange/90 disabled:opacity-50"
            >
              {submitting ? 'Creating your account…' : 'Create account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
